import { Component, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ForgotPasswordService } from './forgot-password.service';
import { CommonModule } from '@angular/common';
import { finalize } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router, ActivatedRoute } from '@angular/router';

type Step = 'email' | 'code' | 'reset' | 'success';

@Component({
  selector: 'app-forgot-password',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './forgot-password.html',
  styleUrls: ['./forgot-password.scss'],
})
export class ForgotPassword {
  step = signal<Step>('email');
  loading = signal(false);
  message = signal<string | null>(null);
  error = signal<string | null>(null);

  emailForm;
  codeForm;
  resetForm;

  private email = signal<string | null>(null);
  private token = signal<string | null>(null);

  constructor(
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private forgotService: ForgotPasswordService,
    private router: Router,
    private route: ActivatedRoute,
  ) {
    this.emailForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
    });

    this.codeForm = this.fb.group({
      code: ['', Validators.required],
    });

    this.resetForm = this.fb.group({
      password: ['', [Validators.required, Validators.minLength(6)]],
      password_confirmation: ['', Validators.required],
    });

    const token = this.route.snapshot.queryParamMap.get('token');
    const email = this.route.snapshot.queryParamMap.get('email');

    if (token && email) {
      this.token.set(token);
      this.email.set(email);
      this.step.set('reset');
    }
  }

  sendEmail() {
    const email = this.emailForm.value.email;

    if (!email) {
      this.error.set('Ingresá un email válido.');
      return;
    }

    this.loading.set(true);
    this.error.set(null);
    this.message.set(null);

    this.forgotService.sendRecoveryEmail(email).subscribe({
      next: (res: { message: string }) => {
        this.snackBar.open(res.message || 'Correo de recuperación enviado correctamente', 'Cerrar', {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
          panelClass: ['success-snackbar'],
        });

        this.email.set(email);
        this.message.set('Te enviamos un enlace a tu correo para restablecer la contraseña.');
      },
      error: (err) => {
        const backendMessage =
          typeof err?.error === 'string'
            ? err.error
            : err?.error?.message || 'Error inesperado';

        this.error.set(backendMessage);

        this.snackBar.open(backendMessage || 'Error al enviar el correo de recuperación', 'Cerrar', {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
          panelClass: ['error-snackbar'],
        });
      },
      complete: () => {
        this.loading.set(false);
      },
    });
  }

  resetPassword() {
    if (this.resetForm.invalid) {
      this.error.set('Completá ambos campos correctamente.');
      return;
    }

    const email = this.email();
    const token = this.token();

    if (!email || !token) {
      this.error.set('El enlace de recuperación es inválido o expiró.');
      return;
    }

    const { password, password_confirmation } = this.resetForm.value;

    this.loading.set(true);
    this.error.set(null);

    this.forgotService
      .resetPasswordWithToken({
        email,
        token,
        password: password!,
        password_confirmation: password_confirmation!,
      })
      .subscribe({
        next: (res: { message: string }) => {
          this.message.set(res.message);
          this.step.set('success');
        },
        error: (err) => {
          this.error.set(
            typeof err?.error === 'string'
              ? err.error
              : err?.error?.message || 'No se pudo actualizar la contraseña.',
          );
        },
        complete: () => {
          this.loading.set(false);
        },
      });
  }

  gotoLogin() {
    this.router.navigate(['/login']);
  }

  openEmailApp() {
    const emailProviders = [
      'mailto:',
      'googlegmail://',
      'ms-outlook://',
      'ymail://',
      'readdle-spark://',
    ];

    for (const provider of emailProviders) {
      window.open(provider, '_blank');
    }
  }
}
